import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { MouseEventHandler, useEffect, useMemo } from 'react'
import { ambulanceCompanySchema } from '@utils/schemas'
import {
  AmbulanceCompanyDto,
  CreateAmbulanceCompanyDto,
  UpdateAmbulanceCompanyDto,
} from '@services/api'
import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { findAll } from '@state/thunks/siteThunk'
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { TextInput } from '@components/inputs'
import {
  createAmbulanceCompany,
  updateAmbulanceCompany,
} from '@state/thunks/ambulanceThunk'
import { enqueueSnackbar } from '@state/reducers/alertReducer'

type AddAmbulanceCompanyProps = {
  row?: AmbulanceCompanyDto
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function AmbulanceForm({ row, onClose }: AddAmbulanceCompanyProps) {
  const dispatch = useAppDispatch()
  const sites = useSelector((state) => state.site.sites)
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CreateAmbulanceCompanyDto>({
    resolver: yupResolver(ambulanceCompanySchema),
    defaultValues: {
      ...row,
      siteIds: row?.sites.map((site) => site.id) || [],
    },
  })

  const sitesOptions: {
    value: string | number
    label: string
  }[] =
    useMemo(() => {
      return sites.datas.map((site) => ({
        value: site.id,
        label: site.label,
      }))
    }, [sites]) || []

  useEffect(() => {
    dispatch(findAll())
  }, [dispatch])

  const onSubmit = (values: CreateAmbulanceCompanyDto) => {
    if (row?.id) {
      const updateAmbulanceCompanyDto: UpdateAmbulanceCompanyDto = {
        ...values,
      }
      dispatch(
        updateAmbulanceCompany({ id: row.id, dto: updateAmbulanceCompanyDto }),
      )
        .unwrap()
        .then(() => {
          dispatch(
            enqueueSnackbar({
              message: 'Ambulance mise à jour avec succès',
              type: 'success',
            }),
          )
        })
        .catch((error) => {
          dispatch(
            enqueueSnackbar({
              message: error.message,
              type: 'error',
            }),
          )
        })
    } else {
      const createAmbulanceCompanyDto: CreateAmbulanceCompanyDto = {
        ...values,
      }
      dispatch(createAmbulanceCompany(createAmbulanceCompanyDto))
        .unwrap()
        .then(() => {
          dispatch(
            enqueueSnackbar({
              message: 'Ambulance créée avec succès',
              type: 'success',
            }),
          )
        })
        .catch((error) => {
          dispatch(
            enqueueSnackbar({
              message: error.message,
              type: 'error',
            }),
          )
        })
    }
    if (onClose) {
      onClose()
    }
  }

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      paddingY={1}
    >
      <Grid item xs={6}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select
            labelId="site-select-label"
            label="Site"
            {...register('siteIds')}
            defaultValue={row?.sites.map((site) => site.id) || []}
          >
            {sitesOptions.map((site) => (
              <MenuItem key={site.value} value={site.value}>
                {site.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <TextInput
          variant="outlined"
          name="name"
          errors={errors}
          control={control}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          variant="outlined"
          name="phone1"
          type="tel"
          errors={errors}
          control={control}
          inputProps={{ maxLength: 10 }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          variant="outlined"
          name="phone2"
          type="tel"
          errors={errors}
          control={control}
          inputProps={{ maxLength: 10 }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          variant="outlined"
          name="email"
          type="email"
          errors={errors}
          control={control}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          variant="outlined"
          name="siret"
          errors={errors}
          control={control}
          // limit to 14 characters
          inputProps={{ maxLength: 14 }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          variant="outlined"
          name="address"
          errors={errors}
          control={control}
        />
      </Grid>
      <Grid item xs={4}>
        <TextInput
          variant="outlined"
          name="zipCode"
          type="number"
          errors={errors}
          control={control}
          inputProps={{ maxLength: 5 }}
        />
      </Grid>
      <Grid item xs={8}>
        <TextInput
          variant="outlined"
          name="city"
          errors={errors}
          control={control}
        />
      </Grid>
      <Grid container gap={2} justifyContent="flex-end" mt={2}>
        {onClose && (
          <Button onClick={onClose} color="primary" variant="outlined">
            Annuler
          </Button>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          color="secondary"
          variant="contained"
        >
          Enregistrer
        </Button>
      </Grid>
    </Grid>
  )
}
